import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Clock, Users, ChefHat, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_RECIPE_IMAGE = "/category-fallbacks/default.jpg";

/** Read-only public view of a recipe by share token. No admin UI, no nav back to home. */
export default function SharedRecipePage() {
  const { token } = useParams();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/share/${token}`);
        if (!cancelled) setRecipe(res.data);
      } catch (e) {
        if (!cancelled) {
          setErr(e.response?.status === 404
            ? "This share link doesn't exist or has been revoked."
            : "Failed to load recipe.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (err) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center">
        <ChefHat className="w-10 h-10 text-muted-foreground mb-4" strokeWidth={1.5} />
        <h1 className="font-serif text-2xl font-bold mb-2">Recipe unavailable</h1>
        <p className="text-muted-foreground max-w-md">{err}</p>
      </div>
    );
  }

  const image = recipe.image_data || recipe.image_url || DEFAULT_RECIPE_IMAGE;

  return (
    <div className="min-h-screen bg-background">
      <div className="relative h-64 md:h-80 overflow-hidden">
        <img src={image} alt={recipe.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
      </div>

      <main className="max-w-3xl mx-auto px-6 -mt-20 relative z-10 pb-16">
        <div className="mb-6">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <Badge variant="secondary">{recipe.category}</Badge>
            {(recipe.tags || []).map((t) => (
              <Badge key={t} variant="outline" className="rounded-full text-xs">
                #{t}
              </Badge>
            ))}
          </div>
          <h1 className="font-serif text-3xl md:text-4xl font-bold text-foreground mb-3">
            {recipe.title}
          </h1>
          {recipe.source && (
            <p className="text-sm text-muted-foreground italic mb-3">From {recipe.source}</p>
          )}
          <div className="flex flex-wrap gap-4 text-muted-foreground">
            {recipe.prep_time && (
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" strokeWidth={1.5} /> Prep {recipe.prep_time}
              </span>
            )}
            {recipe.cook_time && (
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" strokeWidth={1.5} /> Cook {recipe.cook_time}
              </span>
            )}
            {recipe.servings && (
              <span className="flex items-center gap-1.5">
                <Users className="w-4 h-4" strokeWidth={1.5} /> {recipe.servings} servings
              </span>
            )}
          </div>
        </div>

        <Card className="border-border/50 mb-6">
          <CardContent className="p-6">
            <h2 className="font-serif text-xl font-semibold mb-4">Ingredients</h2>
            <ul className="space-y-2">
              {(recipe.ingredients || []).map((ing, i) => (
                <li key={i} className="flex justify-between gap-4 text-sm">
                  <span className="text-foreground">{ing.name}</span>
                  <span className="text-muted-foreground whitespace-nowrap">
                    {ing.amount_imperial}
                    {ing.amount_metric ? ` (${ing.amount_metric})` : ""}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card className="border-border/50 mb-6">
          <CardContent className="p-6">
            <h2 className="font-serif text-xl font-semibold mb-4">Instructions</h2>
            <ol className="space-y-3 list-decimal list-inside marker:text-primary marker:font-semibold">
              {(recipe.instructions || []).map((step, i) => (
                <li key={i} className="text-foreground leading-relaxed pl-1">{step}</li>
              ))}
            </ol>
          </CardContent>
        </Card>

        {recipe.notes && (
          <Card className="border-border/50 mb-6">
            <CardContent className="p-6">
              <h2 className="font-serif text-xl font-semibold mb-2">Notes</h2>
              <p className="whitespace-pre-wrap text-foreground">{recipe.notes}</p>
            </CardContent>
          </Card>
        )}

        <p className="text-center text-xs text-muted-foreground mt-8">
          Shared from Burnsy's Bistro
        </p>
      </main>
    </div>
  );
}
